import { Box } from '@mui/material';
import { ErrorBoundary } from 'react-error-boundary';
import { VariableSizeList } from 'react-window';
import InfiniteLoader from 'react-window-infinite-loader';
import { OuterElementType } from '../AutocompleteWithVirtualList/VirtualListOuterElementType';
import { VirtualListRow } from './VirtualListRow';

interface LazyLoadVirtualListProps<T> {
  data: T[];
  listHeight: number;
  defaultListItemHeight: number;
  listWidth?: string | number;
  loadMoreItems: () => void;
  renderRowComponent: (rowItem: T) => JSX.Element;
  hasNextPage: boolean;
}

export const LazyLoadVirtualList = <T extends unknown>({
  data,
  listHeight,
  defaultListItemHeight,
  listWidth,
  loadMoreItems,
  renderRowComponent,
  hasNextPage,
}: LazyLoadVirtualListProps<T>) => {
  const itemCount = hasNextPage ? data.length + 1 : data.length; // to Accommodate loader

  return (
    <ErrorBoundary
      fallback={
        <Box p={2}>
          Something went wrong while loading the list. Please try again.
        </Box>
      }
    >
      <InfiniteLoader
        isItemLoaded={(index) => !hasNextPage || index < data.length}
        itemCount={itemCount}
        loadMoreItems={loadMoreItems}
      >
        {({ onItemsRendered, ref: variableRef }) => {
          return (
            <VariableSizeList
              itemData={data}
              height={listHeight}
              width={listWidth ?? '100%'}
              ref={variableRef}
              outerElementType={OuterElementType}
              innerElementType="ul"
              itemSize={() => defaultListItemHeight}
              overscanCount={5}
              onItemsRendered={onItemsRendered}
              itemCount={itemCount}
            >
              {({ data, index, style }) => (
                <Box style={style}>
                  <VirtualListRow
                    data={data}
                    index={index}
                    renderRowComponent={renderRowComponent}
                  />
                </Box>
              )}
            </VariableSizeList>
          );
        }}
      </InfiniteLoader>
    </ErrorBoundary>
  );
};
